import {
	getIonValues,
	runMingo,
	unFlatten,
} from '@buddy-technology/ion-helpers';
import mergeWith from 'lodash/mergeWith';
import hasCalculate from './hasCalculate';
import logInfo from './logInfo';

function mergeCustomizer(objValue, srcValue) {
	// if our target has a calculate, replace with the srcValue with the resolved calculation.
	if (hasCalculate(objValue)) {
		return srcValue;
	}
	// preserve obj value if a real value is present
	if (![undefined, ''].includes(objValue)) {
		return objValue;
	}
	return srcValue;
}

/**
 * Computes initial ION values by running calculations on the provided flat application data and setting defaults for missing data.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.flatApplication - The flat application data.
 * @param {Object} [params.incomingData={}] - The incoming data to mergeWith with the flat application data.
 * @param {Object} [params.context={}] - Additional context to use in calculations (variables, offeringOptions, etc.).
 * @param {Object} [params.debug=false] - Whether or not to log debug information.
 * @returns {Object} - An object containing the calculated ION values to initialize our state with.
 */
const getInitialIonValues = ({
	flatApplication,
	incomingData = {},
	context = {},
	debug = false,
}) => {
	const defaultValues = getIonValues(flatApplication, incomingData);
	const calculatedValues = flatApplication.reduce((acc, { id, value }) => {
		if (hasCalculate(value)) {
			const { value: result, error } = runMingo(value._calculate, {
				...unFlatten(defaultValues),
				...context,
			});

			if (debug && error) {
				logInfo(`Error calculating initial value for ${id}: ${error}`);
			}

			if (!error) {
				acc[id] = result;
			}
		}
		return acc;
	}, {});

	// Using the customizer below instead of simply swapping the order of the objects in the mergeWith function because we generally want calculatedValues to take precedence, except when defaultValue is undefined or an empty string.
	const defaultAndCalculatedValues = mergeWith(defaultValues, calculatedValues, mergeCustomizer);
	return defaultAndCalculatedValues;
};

export default getInitialIonValues;
